import React, { FC, useMemo, useCallback, MouseEvent } from 'react'
import cx from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { CalendarMarks, getMark } from './types'

import CoreText from 'components/Core/CoreText'

import styles from './styles.module.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

interface DayProps {
  date: Dayjs
  selected: boolean
  marked?: string
  onSelected: (day: Dayjs) => void
  onCalculateValidDate?: (day: Dayjs) => boolean
}

const Day: FC<DayProps> = ({ date, selected, marked, onSelected, onCalculateValidDate }) => {
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onSelected(date)
    },
    [onSelected, date]
  )

  const isValid = useMemo(() => {
    return onCalculateValidDate ? onCalculateValidDate(date) : false
  }, [onCalculateValidDate, date])

  return (
    <li className={cx(styles.day, { [styles.selected]: selected, [styles[marked as string]]: !!marked })}>
      <button type="button" onClick={handleClick}>
        <CoreText.Paragraph
          className={cx(styles.date, { [styles.valid]: isValid })}
          element="span"
          size="l"
          weight="regular"
        >
          {date.format('D')}
        </CoreText.Paragraph>
      </button>
    </li>
  )
}

interface MonthProps {
  selected: Dayjs
  marks: CalendarMarks
  visibleWeeks: number
  onSelected: (date: Dayjs) => void
  onCalculateValidDate?: (day: Dayjs) => boolean
}

const Month: FC<MonthProps> = ({ selected, marks, visibleWeeks, onSelected, onCalculateValidDate }) => {
  const days = useMemo(() => {
    const totalDays = visibleWeeks * 7
    const firstOfMonth = selected.startOf('M')
    const adjustFrom = visibleWeeks >= 5 ? firstOfMonth : selected
    const start = adjustFrom.startOf('w')

    const theMonth: Dayjs[] = []
    for (let x = 0; x < totalDays; x++) {
      theMonth.push(start.add(x, 'd'))
    }
    return theMonth
  }, [selected, visibleWeeks])
  return (
    <ol className={cx(styles.month)}>
      {days.slice(0, 7).map((day) => (
        <li key={`dow_${day.toDate().getTime()}`} className={styles.dayOfWeek}>
          <CoreText.Paragraph element="span" size="l" weight="light" color="tertiaryBlack">
            {day.format('ddd')}
          </CoreText.Paragraph>
        </li>
      ))}
      {days.map((day) => (
        <Day
          key={`${day.toDate().getTime()}`}
          date={day}
          selected={day.isSame(selected, 'day')}
          marked={getMark(marks, day)}
          onSelected={onSelected}
          onCalculateValidDate={onCalculateValidDate}
        />
      ))}
    </ol>
  )
}

export default Month
